import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Linkedin, Loader2, Download, Copy, CheckCircle } from 'lucide-react';

interface Resume {
  id: string;
  filename: string;
  created_at: string;
}

interface FitAnalysis {
  id: string;
  created_at: string;
  job_analysis_id: string;
}

interface LinkedInProfile {
  headline: string;
  about: string;
  experience: Array<{
    title: string;
    company: string;
    dates: string;
    description: string;
  }>;
  skills: string[];
}

export default function LinkedIn() {
  const { session } = useAuth();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [fitAnalyses, setFitAnalyses] = useState<FitAnalysis[]>([]);
  const [selectedFitId, setSelectedFitId] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [profile, setProfile] = useState<LinkedInProfile | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    loadResumes();
  }, []);

  useEffect(() => {
    if (selectedResumeId) {
      loadFitAnalyses();
    }
  }, [selectedResumeId]);

  async function loadResumes() {
    setLoading(true);
    const { data } = await supabase
      .from('resume_uploads')
      .select('id, filename, created_at')
      .order('created_at', { ascending: false });

    if (data) {
      setResumes(data);
    }
    setLoading(false);
  }

  async function loadFitAnalyses() {
    const { data } = await supabase
      .from('fit_analyses')
      .select('id, created_at, job_analysis_id')
      .eq('resume_id', selectedResumeId)
      .order('created_at', { ascending: false });

    if (data) {
      setFitAnalyses(data);
    }
  }

  async function generateProfile() {
    if (!selectedResumeId) return;

    setGenerating(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-linkedin-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            resumeId: selectedResumeId,
            fitAnalysisId: selectedFitId || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate profile');
      }

      const { profile: generatedProfile } = await response.json();
      setProfile(generatedProfile);
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.message);
    } finally {
      setGenerating(false);
    }
  }

  function copyToClipboard(text: string, section: string) {
    navigator.clipboard.writeText(text);
    setCopied(section);
    setTimeout(() => setCopied(null), 2000);
  }

  function downloadProfile() {
    if (!profile) return;

    let content = '========================================\n';
    content += 'LINKEDIN PROFILE - OPTIMIZED\n';
    content += '========================================\n\n';

    content += 'HEADLINE:\n';
    content += profile.headline + '\n\n';

    content += '========================================\n';
    content += 'ABOUT SECTION:\n';
    content += '========================================\n\n';
    content += profile.about + '\n\n';

    content += '========================================\n';
    content += 'EXPERIENCE:\n';
    content += '========================================\n\n';
    profile.experience.forEach(exp => {
      content += `${exp.title} | ${exp.company}\n`;
      content += `${exp.dates}\n\n`;
      content += `${exp.description}\n\n`;
      content += '---\n\n';
    });

    content += '========================================\n';
    content += 'SKILLS:\n';
    content += '========================================\n\n';
    content += profile.skills.join(' • ') + '\n';

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `linkedin-profile-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <Linkedin className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">LinkedIn Profile Builder</h1>
          </div>
          <p className="text-gray-600">
            Generate an optimized LinkedIn profile from your resume and fit analysis
          </p>
        </div>

        {!profile ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Resume
                </label>
                <select
                  value={selectedResumeId}
                  onChange={e => setSelectedResumeId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="">Choose a resume...</option>
                  {resumes.map(resume => (
                    <option key={resume.id} value={resume.id}>
                      {resume.filename} - {new Date(resume.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>

              {selectedResumeId && fitAnalyses.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Use Fit Analysis (Optional)
                  </label>
                  <select
                    value={selectedFitId}
                    onChange={e => setSelectedFitId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">None - General profile</option>
                    {fitAnalyses.map(fit => (
                      <option key={fit.id} value={fit.id}>
                        Fit Analysis - {new Date(fit.created_at).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={generateProfile}
                disabled={!selectedResumeId || generating}
                className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Profile...
                  </>
                ) : (
                  <>
                    <Linkedin className="w-5 h-5 mr-2" />
                    Generate LinkedIn Profile
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6 flex gap-3">
              <button
                onClick={downloadProfile}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
              >
                <Download className="w-5 h-5 mr-2" />
                Download
              </button>
              <button
                onClick={() => setProfile(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Generate New
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Headline</h3>
                  <button
                    onClick={() => copyToClipboard(profile.headline, 'headline')}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                  >
                    {copied === 'headline' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-700">{profile.headline}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {profile.headline.length} / 120 characters
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">About Section</h3>
                  <button
                    onClick={() => copyToClipboard(profile.about, 'about')}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                  >
                    {copied === 'about' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{profile.about}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {profile.about.length} / 2000 characters
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Experience Entries</h3>
                <div className="space-y-4">
                  {profile.experience.map((exp, i) => (
                    <div key={i} className="border-l-4 border-blue-600 pl-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-bold text-gray-900">{exp.title}</h4>
                          <p className="text-sm text-gray-600">{exp.company} • {exp.dates}</p>
                        </div>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              `${exp.title}\n${exp.company}\n${exp.dates}\n\n${exp.description}`,
                              `exp-${i}`
                            )
                          }
                          className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                        >
                          {copied === `exp-${i}` ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-gray-700 text-sm">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Skills ({profile.skills.length})</h3>
                  <button
                    onClick={() => copyToClipboard(profile.skills.join(', '), 'skills')}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                  >
                    {copied === 'skills' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
